/* -----------------------------------------------------------------------------

THIS FILE DOES NOT USE AUTOMATIC DOCUMENTATION

YOU WILL NEED TO MANUALLY UPDATE Card.mdx WHEN CHANGING PROPS

Why, you ask? Well, Storybook 8 isn't parsing things right. So we have to do it
this way right now. Cool.

----------------------------------------------------------------------------- */

import { mergeClassNames } from '@/utils/mergeClassNames';
import clsx from 'clsx';
import { HTMLAttributes, ReactNode } from 'react';
import {
  Button,
  ButtonProps,
  Link,
  LinkProps,
  ToggleButton,
  ToggleButtonGroup,
  ToggleButtonGroupProps,
  ToggleButtonProps,
} from 'react-aria-components';
import styles from './Card.module.scss';

type CardRootBaseProps = {
  variant?: 'elevated' | 'outlined' | 'filled';
  padding?: 'medium' | 'small' | 'none';
  radius?: 'default' | 'inset' | 'inset-medium';
};

export type CardRootContainerProps = CardRootBaseProps &
  HTMLAttributes<HTMLElement>;

export type CardRootLinkProps = CardRootBaseProps & LinkProps;
export type CardRootButtonProps = CardRootBaseProps & ButtonProps;
export type CardRootToggleButtonProps = CardRootBaseProps & ToggleButtonProps;

/**
 * A card that functions as a non-interactive container. You may put interactive
 * content inside this root.
 */
export const CardRootContainer = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: CardRootContainerProps): ReactNode => {
  return (
    <div
      className={clsx(styles.root, className)}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that functions as a link. Use `href` and `DesignSystemProvider` to
 * ensure client side routing.
 *
 * ---
 *
 * @see https://react-spectrum.adobe.com/react-spectrum/Link.html#link
 * @see DesignSystemProvider
 */
export const CardRootLink = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: CardRootLinkProps): ReactNode => {
  return (
    <Link
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that functions as a button.
 *
 * ---
 *
 * **NOTE:** _You may **NOT** put interactive children inside this component._
 *
 * ---
 *
 * @see https://react-spectrum.adobe.com/react-spectrum/Button.html#button
 */
export const CardRootButton = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: CardRootButtonProps): ReactNode => {
  return (
    <Button
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

/**
 * A card that can be used as a toggle button when alone. As a group, it
 * implements the ARIA radiogroup (single selection) or ARIA toolbar (multiple
 * selection).
 *
 * When wrapped in a CardGroupToggleButton, use the arrow keys to navigate between
 * buttons within the group.
 *
 * ---
 *
 * **NOTE:** _Wrap in CardGroupToggleButton when using multiple toggle button cards
 * together. The prop `id` is required to keep track of selected state._
 *
 * Use React's `useId()` to generate a safe ID prefix for your component if it
 * may render more than once one a page.
 *
 * ---
 *
 * **NOTE:** _You may **NOT** put interactive children inside this component._
 *
 * ---
 *
 * @see
 * https://react-spectrum.adobe.com/react-aria/CardGroupToggleButton.html#togglebuttongroup
 */
export const CardRootToggleButton = ({
  className,
  variant = 'elevated',
  padding = 'medium',
  radius = 'default',
  ...props
}: CardRootToggleButtonProps): ReactNode => {
  return (
    <ToggleButton
      className={(renderProps) => {
        return mergeClassNames(
          [styles.root, styles.interactive, className],
          renderProps
        );
      }}
      data-variant={variant}
      data-padding={padding}
      data-radius={radius}
      {...props}
    />
  );
};

type CardHeaderFooterBaseProps = HTMLAttributes<HTMLElement> & {
  children: ReactNode;
  divider?: 'visible' | 'hidden';
  padding?: CardRootBaseProps['padding'];
};

export type CardHeaderProps = CardHeaderFooterBaseProps;

/**
 * The header content of a `CardRoot*` element (optional)
 */
export const CardHeader = ({
  className,
  padding, // default value omitted, to inherit from parent via CSS
  divider = 'visible',
  ...props
}: CardHeaderProps): ReactNode => {
  return (
    <div
      className={clsx(styles.header, className)}
      data-padding={padding}
      data-divider={divider}
      {...props}
    />
  );
};

export type CardFooterProps = CardHeaderFooterBaseProps;

/**
 * The footer content of a `CardRoot*` element (optional)
 */
export const CardFooter = ({
  className,
  padding, // default value omitted, to inherit from parent via CSS
  divider = 'visible',
  ...props
}: CardFooterProps): ReactNode => {
  return (
    <div
      className={clsx(styles.footer, className)}
      data-padding={padding}
      data-divider={divider}
      {...props}
    />
  );
};

export type CardBodyProps = HTMLAttributes<HTMLElement> & {
  padding?: CardRootBaseProps['padding'];
  children: ReactNode;
};

/**
 * The body content of a `CardRoot*` element (required)
 */
export const CardBody = ({
  className,
  padding,
  ...props
}: CardBodyProps): ReactNode => {
  return (
    <div
      className={clsx(styles.body, className)}
      data-padding={padding}
      {...props}
    />
  );
};

export type CardGroupToggleButtonProps = ToggleButtonGroupProps;

export const CardGroupToggleButton = (props: CardGroupToggleButtonProps) => {
  return <ToggleButtonGroup {...props} />;
};
